import { awaitJSON, CreativeLambdaFetch } from "../../../utils/fetch-utils";
import { CompanyInfo } from "../../../redux/company";
import { CreativeMap } from "../../../redux/creative";
import { Dropdown, DropdownToggleType, OverlayTrigger, Tooltip } from "../../../Components";
import { FieldConfig, LANGUAGES, AddCreativeMediatypeConfig } from "../AddCreativeConfigsUtils";
import { Form } from "react-bootstrap";
import { LINEAR, STREAMING_PLUS } from "@blisspointmedia/bpm-types/dist/MetricsTable";
import { MdEdit, MdInfoOutline, MdSave } from "react-icons/md";
import { TODAY } from "@blisspointmedia/bpm-types/dist/RelativeDatePicker";
import * as R from "ramda";
import { ABCDCompanyOptions } from "@blisspointmedia/bpm-types/dist/pgTables/ABCDCompanyOptions";

type TVADAssetDetails = Record<string, any>;

interface TVADCreative {
  avail: string;
  isci: string;
  language: string;
  length: string;
  name: string;
}

const CUSTOM_ATTRIBUTE_KEYWORD = "_CUSTOM_ATTRIBUTE";

export const getCustomAttributes = async (cid: string): Promise<Record<string, FieldConfig>> => {
  const res = await CreativeLambdaFetch("/get_creative_options", {
    params: { cid },
  });
  const { categoryData, conceptData, insightsCategoryData } = await awaitJSON(res);
  const customAttributes: Record<string, FieldConfig> = {
    concept: {
      label: "Concept",
      order: 1,
      type: "dropdown",
      options: R.map(
        conceptEntry => ({
          conceptId: conceptEntry.id,
          label: conceptEntry.concept,
          value: conceptEntry.id,
        }),
        conceptData
      ),
    },
  };
  if (insightsCategoryData && insightsCategoryData.length) {
    customAttributes.insightCategories = {
      label: "Insight Categories",
      options: insightsCategoryData,
      order: 2,
      type: "dropdown",
    };
  }
  let order = 2;
  for (const categoryEntry of categoryData) {
    if (categoryEntry && categoryEntry.options && categoryEntry.options.length) {
      order++;
      customAttributes[`${categoryEntry.name}${CUSTOM_ATTRIBUTE_KEYWORD}`] = {
        collapsable: true,
        label: categoryEntry.name,
        multiSelect: true,
        options: R.map(option => ({ label: option, value: option }), categoryEntry.options),
        order,
        type: "radio",
      };
    }
  }
  return customAttributes;
};

interface GenerateTVADCreativesArgs {
  assetDetails: TVADAssetDetails;
  companyInfo: CompanyInfo;
  creativeCode: string;
  creativeName: string;
  version: string;
}

const generateCreatives = (args: GenerateTVADCreativesArgs): TVADCreative[] => {
  const { assetDetails, companyInfo, creativeCode, creativeName, version } = args;
  const languageMap = assetDetails.language;
  const lengthMap = assetDetails.length;
  const mediatypeMap = assetDetails.mediatypes;
  const creatives: TVADCreative[] = [];
  const companyAbbreviation = companyInfo.name_abbrev;
  if (creativeCode && version) {
    for (const language of R.keys(languageMap) as string[]) {
      const languageLetter = R.path([language as string, "abbrev"], LANGUAGES) || "E";
      for (const length of R.keys(lengthMap) as string[]) {
        const isciInit = `${companyAbbreviation}${creativeCode}${version}${length}${languageLetter}`;
        const isci = `${isciInit}NH`;
        const name = `${creativeName}${
          language && language !== "english"
            ? ` (${language.charAt(0).toUpperCase() + language.slice(1)})`
            : ""
        }`;
        creatives.push({
          avail: "National",
          isci,
          language,
          length,
          name,
        });
        if (mediatypeMap.linear && assetDetails.includeLocalAvails) {
          creatives.push({
            avail: "Local",
            isci: `${isciInit}LH`,
            language,
            length,
            name,
          });
        }
      }
    }
  }
  return creatives;
};

interface RenderGeneratedCreativesArgs {
  assetDetails: TVADAssetDetails;
  companyInfo: CompanyInfo;
  creativeCode: string;
  creativeIDOverrideMap: Record<string, string>;
  editingCreativeNames: boolean;
  generatedCreatives: TVADCreative[];
  setCreativeIDOverrideMap: Function;
  setEditingCreativeNames: Function;
  version: string;
}

const renderGeneratedCreatives = (args: RenderGeneratedCreativesArgs): JSX.Element => {
  const {
    assetDetails,
    companyInfo,
    creativeCode,
    creativeIDOverrideMap,
    editingCreativeNames,
    generatedCreatives,
    setCreativeIDOverrideMap,
    setEditingCreativeNames,
    version,
  } = args;
  const companyAbbreviation = companyInfo.name_abbrev;
  const languageMap = assetDetails.language;
  const lengthMap = assetDetails.length;
  const creativesGroupedByLanguage = R.groupBy(R.prop("language"), generatedCreatives);
  return (
    <div className="creativeIDsSection">
      <div className="sectionHeaderContainer">
        <div className="headerSubHeader">
          <div className="sectionHeader">ISCIs</div>
          <div className="subHeader">
            {companyAbbreviation} + {creativeCode ? creativeCode : "_"} + {version ? version : "_"}{" "}
            + {R.keys(lengthMap).length ? `${R.keys(lengthMap)[0] as string} +` : ""}{" "}
            {R.keys(languageMap).length ? (R.keys(languageMap)[0] as string).toUpperCase()[0] : "E"}{" "}
            + NH
          </div>
        </div>
        <div
          className={`editIcon ${
            generatedCreatives && generatedCreatives.length ? "" : "disabled"
          }`}
          onClick={() => setEditingCreativeNames(!editingCreativeNames)}
        >
          {editingCreativeNames ? <MdEdit /> : <MdSave />}
        </div>
      </div>
      <div className="creativeIDsContainer">
        {R.map(
          language => (
            <div className="languageContainer">
              <div className="languageLabel">{LANGUAGES[language].label}</div>
              <div className="creativeIDs">
                {R.map(isci => {
                  const overridenISCI = R.defaultTo(isci.isci, creativeIDOverrideMap[isci.isci]);
                  return editingCreativeNames ? (
                    <Form.Control
                      id={isci.isci}
                      type="text"
                      value={overridenISCI}
                      onChange={e =>
                        setCreativeIDOverrideMap({
                          ...creativeIDOverrideMap,
                          [isci.isci]: e.target.value,
                        })
                      }
                    />
                  ) : (
                    <div className="creativeIDContainer" key={isci.isci}>
                      <div className="length">{isci.length}s</div>
                      <div className="avail">{isci.avail}</div>
                      <div className="creativeID">{overridenISCI}</div>
                    </div>
                  );
                }, creativesGroupedByLanguage[language])}
              </div>
              <div className="border" />
            </div>
          ),
          R.filter(
            language =>
              !R.isNil(creativesGroupedByLanguage[language]) &&
              creativesGroupedByLanguage[language].length > 0,
            R.keys(LANGUAGES)
          ) as string[]
        )}
      </div>
    </div>
  );
};

interface RenderDuplicateCreativesArgs {
  assetDetails: TVADAssetDetails;
  creativeMap: { creativeMap: CreativeMap };
  duplicateCreativeName: string;
  generatedCreatives: TVADCreative[];
  setDuplicateCreativeName: Function;
}

const renderDuplicateCreatives = (args: RenderDuplicateCreativesArgs): JSX.Element => {
  const {
    assetDetails,
    creativeMap,
    duplicateCreativeName,
    generatedCreatives,
    setDuplicateCreativeName,
  } = args;
  const creativeNameMap = {};
  const creativesGroupedByLanguage = R.groupBy(R.prop("language"), generatedCreatives);
  if (creativeMap && creativeMap.creativeMap && assetDetails && assetDetails.mediatypes) {
    for (const creativeID of R.keys(creativeMap.creativeMap)) {
      const creative = creativeMap.creativeMap[creativeID];
      let hasMediaType = false;
      for (const mediatype of creative.media_types) {
        if (R.keys(assetDetails.mediatypes).includes(mediatype)) {
          hasMediaType = true;
          break;
        }
      }
      if (hasMediaType) {
        creativeNameMap[creative.name] = {
          ...R.defaultTo({}, creativeNameMap[creative.name]),
          [R.toLower(creative.language)]: {
            [creative.length]: {
              [creative.avail]: {
                creativeID,
              },
            },
          },
        };
      }
    }
  }
  return (
    <div className="duplicateIDsSection">
      <div className="headerContainer">
        <div className="label">Same video file:</div>
        <OverlayTrigger
          overlay={
            <Tooltip
              children={
                "If the creative you are creating uses the same asset as an existing creative, please mark it here. Keep in mind that this creative should have the same avails, languages, and lengths available."
              }
            />
          }
        >
          <div className="infoIcon">
            <MdInfoOutline />
          </div>
        </OverlayTrigger>
      </div>
      <Dropdown
        type={DropdownToggleType.OUTLINED}
        value={duplicateCreativeName}
        options={R.map(
          creativeName => ({ label: creativeName, value: creativeName }),
          R.keys(creativeNameMap)
        )}
        onChange={creativeName => setDuplicateCreativeName(creativeName)}
        placeholder="Select Source Creative Name"
      />
      <div className="duplicateIDsContainer">
        {R.map(
          language => (
            <div className="languageContainer">
              <div className="duplicateIDs">
                {R.map(isci => {
                  const creativeID =
                    creativeNameMap[duplicateCreativeName] &&
                    creativeNameMap[duplicateCreativeName][language] &&
                    creativeNameMap[duplicateCreativeName][language][isci.length] &&
                    (creativeNameMap[duplicateCreativeName][language][isci.length][isci.avail[0]] ||
                      creativeNameMap[duplicateCreativeName][language][isci.length].N)
                      ? R.defaultTo(
                          creativeNameMap[duplicateCreativeName][language][isci.length].N,
                          creativeNameMap[duplicateCreativeName][language][isci.length][
                            isci.avail[0]
                          ]
                        ).creativeID
                      : undefined;

                  return (
                    <div className="creativeIDContainer" key={isci.isci}>
                      <div className={`creativeID ${creativeID ? "" : "missing"}`}>
                        {creativeID ? creativeID : "NOT AVAILABLE"}
                      </div>
                    </div>
                  );
                }, creativesGroupedByLanguage[language])}
              </div>
              <div className="border" />
            </div>
          ),
          R.filter(
            language =>
              !R.isNil(creativesGroupedByLanguage[language]) &&
              creativesGroupedByLanguage[language].length > 0,
            R.keys(LANGUAGES)
          ) as string[]
        )}
      </div>
    </div>
  );
};

interface SaveCreativeArgs {
  assetDetails: TVADAssetDetails;
  company: string;
  creativeIDOverrideMap: Record<string, string>;
  creativeMap: { creativeMap: CreativeMap };
  creativeNameOverrideMap: Record<string, string>;
  duplicateCreativeName: string;
  generatedCreatives: TVADCreative[];
  setAreYouSure: Function;
  setError: Function;
  setSaving: Function;
  skipMap: Record<string, boolean>;
}

const saveNewCreatives = async (args: SaveCreativeArgs): Promise<void> => {
  const {
    assetDetails,
    company,
    creativeIDOverrideMap,
    creativeMap,
    creativeNameOverrideMap,
    duplicateCreativeName,
    generatedCreatives,
    setAreYouSure,
    setError,
    setSaving,
    skipMap,
  } = args;
  const {
    b2bCreative1,
    b2bCreative2,
    creativeCode,
    creativeName,
    customAttributes,
    dates = { start: TODAY },
    mediatypes,
    parentCreative = "",
    reportingCreative = "",
  } = assetDetails;
  const backToBackISCIS: { first?: string; second?: string } = {};
  const checkedAttributeOptions = {};
  const duplicateCreativesMap = {};
  const hasChosenLanguage = R.keys(assetDetails.language).length > 0;
  const hasChosenLength = R.keys(assetDetails.length).length > 0;
  const hasChosenMediaType = R.keys(assetDetails.mediatypes).length > 0;
  const isBackToBack = assetDetails.backToBackCreative === "yes";
  const selectedConcept = { conceptId: assetDetails.concept };
  if (b2bCreative1) {
    backToBackISCIS.first = b2bCreative1;
  }
  if (b2bCreative2) {
    backToBackISCIS.second = b2bCreative2;
  }
  for (const key of R.keys(customAttributes)) {
    if ((key as string).includes(CUSTOM_ATTRIBUTE_KEYWORD)) {
      checkedAttributeOptions[(key as string).replace(CUSTOM_ATTRIBUTE_KEYWORD, "")] =
        customAttributes[key];
    }
  }
  if (creativeMap && creativeMap.creativeMap && assetDetails && assetDetails.mediatypes) {
    for (const creativeID of R.keys(creativeMap.creativeMap)) {
      const creative = creativeMap.creativeMap[creativeID];
      if (creative.name !== duplicateCreativeName) {
        continue;
      }
      let hasMediaType = false;
      for (const mediatype of creative.media_types) {
        if (R.keys(assetDetails.mediatypes).includes(mediatype)) {
          hasMediaType = true;
          break;
        }
      }
      if (hasMediaType) {
        duplicateCreativesMap[R.toLower(creative.language)] = {
          ...R.defaultTo({}, duplicateCreativesMap[R.toLower(creative.language)]),
          [creative.length]: {
            [creative.avail]: {
              creativeID,
            },
          },
        };
      }
    }
  }
  setSaving(true);
  if (!creativeName) {
    setError({
      title: "Name Required",
      message: "Creative Name is required.",
    });
    setSaving(false);
    return;
  }
  let badMatch = creativeName.match(/[^a-zA-Z0-9-&!?/:()$%+. ]/);
  if (badMatch) {
    setError({
      title: "Name Has Illegal Characters",
      message: (
        <>
          <p>
            Creative names can only have numbers, letters, spaces, or any of{" "}
            <code>"-&amp;!?/:()$%+."</code>.
          </p>
          <p>
            Your creative name has the character <code>"{badMatch[0]}"</code>
          </p>
        </>
      ),
    });
    setSaving(false);
    return;
  }
  if (!creativeCode) {
    setError({
      title: "Creative Code Required",
      message: "Creative Code is required.",
    });
    setSaving(false);
    return;
  }
  if (creativeCode.length !== 3) {
    try {
      await setAreYouSure({
        title: "Creative Code isn't three letters",
        message: `Creative Codes should generally be three letters long. You entered one with ${
          creativeCode.length
        } letter${creativeCode.length === 1 ? "" : "s"}. Are you sure you want to continue?`,
        okayText: `Make ISCIs with ${creativeCode.length} letter codes`,
      });
    } catch (e) {
      setSaving(false);
      return;
    }
  }
  if (!isBackToBack && R.isEmpty(selectedConcept)) {
    setError({
      title: "No Concept",
      message: "Concept is required. None specified.",
    });
    setSaving(false);
    return;
  }
  if (isBackToBack && (!b2bCreative1 || !b2bCreative2)) {
    setError({
      title: "ISCI 1 and ISCI 2 Required",
      message: "ISCI 1 and ISCI 2 are both required for a Back-to-Back asset.",
    });
    setSaving(false);
    return;
  }
  if (!hasChosenLanguage) {
    setError({
      title: "No Language",
      message: "You must pick at least one language.",
    });
    setSaving(false);
    return;
  }
  if (!hasChosenLength) {
    setError({
      title: "No Length",
      message: "You must pick at least one length.",
    });
    setSaving(false);
    return;
  }
  if (!hasChosenMediaType) {
    setError({
      title: "No Media Type",
      message: "You must pick at least one media type.",
    });
    setSaving(false);
    return;
  }
  if (!generatedCreatives.length) {
    setError({
      title: "No Creatives",
      message: "You aren't generating any creatives. You may have left a field blank.",
    });
    setSaving(false);
    return;
  } else {
    const existingCreatives: string[] = [];
    for (const creative of generatedCreatives) {
      if (creative && creative.isci) {
        const creativeID =
          creativeIDOverrideMap && creativeIDOverrideMap[creative.isci]
            ? creativeIDOverrideMap[creative.isci]
            : creative.isci;
        if (creativeMap.creativeMap[creativeID]) {
          existingCreatives.push(creativeID);
        }
      }
    }
    if (existingCreatives.length) {
      setError({
        title: "Existing Creatives",
        message: `The following creatives already exist in the creative map, please rename them: ${existingCreatives}`,
      });
    }
  }
  const lines: {
    avail: string;
    isci: string;
    language: string;
    length: string;
    name: string;
    file?: string;
    reporting?: string;
    clickthrough?: string;
  }[] = [];
  for (const creative of generatedCreatives) {
    const { avail, language, length } = creative;
    const name =
      creativeNameOverrideMap && creativeNameOverrideMap[creative.language]
        ? creativeNameOverrideMap[creative.language]
        : creative.name;
    if (!name) {
      setError({
        title: "Name Required",
        message: "Creative Name is required.",
      });
      setSaving(false);
      return;
    }
    badMatch = name.match(/[^a-zA-Z0-9-&!?/:()$%+. ]/);
    if (badMatch) {
      setError({
        title: "Name Has Illegal Characters",
        message: (
          <>
            <p>
              Creative names can only have numbers, letters, spaces, or any of{" "}
              <code>"-&amp;!?/:()$%+."</code>.
            </p>
            <p>
              Your creative name has the character <code>"{badMatch[0]}"</code>
            </p>
          </>
        ),
      });
      setSaving(false);
      return;
    }
    let defaultClickthrough;
    for (const language of R.keys(LANGUAGES)) {
      if (assetDetails[`${language}_clickthrough_url`]) {
        defaultClickthrough = assetDetails[`${language}_clickthrough_url`];
        break;
      }
    }
    const clickthrough = assetDetails[`${creative.language}_clickthrough_url`]
      ? assetDetails[`${creative.language}_clickthrough_url`]
      : defaultClickthrough;
    if (clickthrough && !R.test(/^https:\/\//, clickthrough)) {
      setError({
        title: "Click-through HTTPS",
        message: `Click-through URLs must begin with "https://". You entered "${clickthrough}"`,
      });
      setSaving(false);
      return;
    }
    const isci =
      creativeIDOverrideMap && creativeIDOverrideMap[creative.isci]
        ? creativeIDOverrideMap[creative.isci]
        : creative.isci;
    if (skipMap[isci]) {
      continue;
    }
    if (R.test(/[^\d\w]/, isci)) {
      setError({
        title: "Invalid ISCI",
        message: `ISCIs must only be letters and numbers. You entered "${isci}"`,
      });
      setSaving(false);
      return;
    }
    let file = isci;
    if (
      duplicateCreativesMap[creative.language] &&
      duplicateCreativesMap[creative.language][creative.length] &&
      duplicateCreativesMap[creative.language][creative.length][creative.avail[0]]
    ) {
      file =
        duplicateCreativesMap[creative.language][creative.length][creative.avail[0]].creativeID;
    } else if (creativeIDOverrideMap[isci]) {
      file = creativeIDOverrideMap[isci];
    } else if (avail === "Local") {
      for (const nationalCreative of generatedCreatives) {
        if (
          creative.language === nationalCreative.language &&
          creative.length === nationalCreative.length
        ) {
          file = creativeIDOverrideMap[nationalCreative.isci]
            ? creativeIDOverrideMap[nationalCreative.isci]
            : nationalCreative.isci;
          break;
        }
      }
    }
    lines.push({
      avail: avail.charAt(0), // Assuming the only two avails are National and Local
      clickthrough,
      file,
      isci,
      language: LANGUAGES[language].label,
      length,
      name,
      reporting: reportingCreative,
    });
  }
  if (!lines.length) {
    setError({
      title: "No ISCIs",
      message: "You have to generate at least one ISCI",
    });
    setSaving(false);
    return;
  }
  const payload = {
    backToBackISCIS,
    checkedAttributeOptions,
    company,
    dates,
    lines,
    mediaTypes: R.keys(mediatypes),
    parent: parentCreative,
    selectedConcept,
  };
  if (dates.end && dates.start > dates.end) {
    setError({
      title: "Invalid Date Range",
      message: "You cannot have your creatives start after they end.",
    });
    setSaving(false);
    return;
  }
  if (!dates.start) {
    try {
      await setAreYouSure({
        title: "Never Live",
        message:
          "You did not specify a live start date. This means these creatives will never go live. Are you sure you want to continue?",
        okayText: "Yes, Create Non-Live Creatives",
      });
    } catch (e) {
      setSaving(false);
      return;
    }
  }
  let response;
  try {
    let res = await CreativeLambdaFetch("/bulk_add", {
      method: "post",
      body: payload,
    });
    response = await awaitJSON(res);
  } catch (e) {
    const error = e as Error;
    if ((error.message || "").includes("The following creative name is already taken")) {
      try {
        await setAreYouSure({
          title: "Creative Already Defined",
          message: `${error.message} The ISCIs you're currently adding might be grouped together with these existing creatives on certain pages. Are you sure you want to continue?`,
          okayText: "Add Them Anyway",
        });
      } catch (e) {
        setSaving(false);
        return;
      }
      try {
        const res = await CreativeLambdaFetch("/bulk_add", {
          method: "post",
          body: {
            ...payload,
            skipNameCheck: true,
          },
        });
        response = await awaitJSON(res);
      } catch (e) {
        const error = e as Error;
        const message = `Failed to add ISCIs. ${error.message}`;
        setError({
          message,
          reportError: e,
        });
        setSaving(false);
        return;
      }
    } else {
      const error = e as Error;
      const message = `Failed to add ISCIs. ${error.message}`;
      setError({ message, reportError: e });
      setSaving(false);
      return;
    }
  }
  await setError({
    title: "Success",
    variant: "success",
    message: `Successfully added ${response.insertCount} creative${
      response.insertCount === 1 ? "" : "s"
    } and ${response.dateCount} live date range${response.dateCount === 1 ? "" : "s"}.`,
    onOkay: () => window.location.reload(),
  });
};

interface UpdateDetailsArgs {
  abcdCompanyOptions: ABCDCompanyOptions;
  assetDetails: TVADAssetDetails;
  creativeMap: { creativeMap: CreativeMap };
  details: Record<string, FieldConfig>;
}

const updateDetails = (args: UpdateDetailsArgs): Record<string, FieldConfig> => {
  const { abcdCompanyOptions, assetDetails, creativeMap, details } = args;
  if (abcdCompanyOptions && abcdCompanyOptions.brand_name) {
    details.brandName.options = R.map(
      option => ({ label: option, value: option }),
      abcdCompanyOptions.brand_name
    );
  }
  if (assetDetails && assetDetails.backToBackCreative && assetDetails.backToBackCreative.yes) {
    const options: { label: string; value: string }[] = [];
    for (const creativeID of R.keys(creativeMap.creativeMap)) {
      options.push({
        label: `${creativeMap.creativeMap[creativeID].name} (${creativeID})`,
        value: `${creativeID}`,
      });
    }
    details.b2bCreative1 = {
      type: "dropdown",
      label: "Back to Back Creative 1",
      multiSelect: true,
      options,
      order: 7,
    };
    details.b2bCreative2 = {
      type: "dropdown",
      label: "Back to Back Creative 2",
      multiSelect: true,
      options,
      order: 8,
    };
  } else {
    delete details.b2bCreative1;
    delete details.b2bCreative2;
  }
  if (
    assetDetails &&
    assetDetails.mediatypes &&
    R.keys(assetDetails.mediatypes).includes(STREAMING_PLUS[0]) &&
    assetDetails.language &&
    R.keys(assetDetails.language).length
  ) {
    let order = 9;
    for (const language of R.keys(LANGUAGES)) {
      if (assetDetails && assetDetails.language && assetDetails.language[language]) {
        details[`${language as string}_clickthrough_url`] = {
          type: "text",
          mediatypes: [STREAMING_PLUS[0]],
          label: `${LANGUAGES[language as string].label} Clickthrough URL`,
          order,
        };
        order++;
      } else {
        delete details[`${language as string}_clickthrough_url`];
      }
    }
  } else {
    for (const language of R.keys(LANGUAGES)) {
      delete details[`${language as string}_clickthrough_url`];
    }
  }
  return details;
};

const details: Record<string, FieldConfig> = {
  mediatypes: {
    type: "radio",
    label: "Media Types",
    multiSelect: true,
    options: [
      { value: "streaming", label: "Streaming" },
      { value: "linear", label: "Linear" },
    ],
    order: 1,
  },
  dates: {
    type: "dates",
    label: "Dates",
    order: 2,
  },
  length: {
    type: "radio",
    label: "Length",
    multiSelect: true,
    options: [
      { value: "15", label: "15" },
      { value: "30", label: "30" },
      { value: "60", label: "60" },
    ],
    order: 3,
  },
  language: {
    type: "radio",
    label: "Language",
    multiSelect: true,
    options: [
      { value: "english", label: "English" },
      { value: "spanish", label: "Spanish" },
      { value: "french", label: "French" },
    ],
    order: 4,
  },
  backToBackCreative: {
    type: "radio",
    label: "Back-to-back Creative",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
    order: 5,
  },
  includeLocalAvails: {
    type: "radio",
    mediatypes: [LINEAR],
    label: "Include Local Avails (Linear)",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
  },
};

export const customAttributesConfig: Record<string, FieldConfig> = {
  concept: {
    type: "dropdown",
    label: "Concept",
  },
  insightCategories: {
    type: "dropdown",
    label: "Insight Categories",
  },
};

export const TVAD_CONFIG: AddCreativeMediatypeConfig = {
  details,
  generateCreatives,
  getCustomAttributes,
  renderDuplicateCreatives,
  renderGeneratedCreatives,
  saveNewCreatives,
  updateDetails,
};
